import { faBell, faBellSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React, { useRef, useState } from 'react';
import { useNotifications } from '../Context/NotificationProvider';
import { useNavigate } from '../ProtectedRoute';
import { formatDateTime } from '../Utils/Time';
import Button from './Button';
import LoadingIcon from './LoadingIcon';

// Add CSS keyframes for the bell jingling animation
const bellJingleKeyframes = `
@keyframes jingle {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-25deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(25deg); }
  100% { transform: rotate(0deg); }
}
`;

export const NotificationIcon: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead, doNotDisturb } =
    useNotifications();
  const navigate = useNavigate();
  const iconRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasUnread = notifications.some((n) => !n.readAt);

  const handleNotificationClick = async (id: string, link: string) => {
    // Mark as read (will update in memory first)
    markAsRead(id);

    // Navigate to the notification link
    navigate(link);

    // Return a resolved promise as required by DropdownMenu
    return Promise.resolve();
  };

  return (
    <>
      {/* Add style tag for the keyframes */}
      <style>{bellJingleKeyframes}</style>
      <div className="relative">
        <div
          ref={iconRef}
          className="cursor-pointer"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <div className="w-8 h-8 max-sm:h-7 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center">
            {!notifications ? (
              <LoadingIcon size={4} color="blue-600" />
            ) : (
              <FontAwesomeIcon
                icon={doNotDisturb ? faBellSlash : faBell}
                className={`${hasUnread && !doNotDisturb ? 'text-red-600 w-5 h-5' : 'text-blue-600 w-4 h-4'}  ${
                  hasUnread && !doNotDisturb
                    ? 'animate-[jingle_0.5s_ease-in-out_infinite]'
                    : ''
                }`}
              />
            )}
          </div>
        </div>
        {isOpen && (
          <div className="absolute mt-2 w-96 bg-white rounded-md shadow-lg z-20 overflow-hidden right-0">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Notifications</h2>
              <Button
                variant={hasUnread ? 'primary' : 'secondary'}
                onClick={markAllAsRead}
                disabled={!hasUnread}
                inProgressText="Marking..."
                className="!rounded-3xl !py-1 !px-2 !text-xs"
              >
                Mark all as read
              </Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {notifications.length > 0 ? (
                <div>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        handleNotificationClick(
                          notification.id,
                          notification.link,
                        )
                      }
                    >
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-sm text-gray-500">
                          {formatDateTime(
                            DateTime.fromJSDate(notification.createdAt),
                          )}
                        </div>
                        <div
                          className={`w-3 h-3 rounded-full flex-shrink-0
                        ${
                          notification.readAt
                            ? 'border-2 border-blue-600 bg-white'
                            : 'bg-blue-600'
                        }`}
                        ></div>
                      </div>
                      <div>
                        <div
                          className={`text-left ${!notification.readAt ? 'font-semibold text-gray-800' : 'text-gray-700'}`}
                        >
                          {notification.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No notifications
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
